/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { Box } from '@mui/material';
import type { Feature } from 'geojson';
import React from 'react';
import type { Position, Source } from '@opengeoweb/shared';
import {
  ToolContainerDraggable,
  useWheelStopPropagation,
} from '@opengeoweb/shared';
import SelectedFeature from './SelectedFeature';
import { useCoreTranslation } from '../../utils/i18n';

export interface SelectedFeaturesDialogProps {
  onClose: () => void;
  isOpen: boolean;
  onMouseDown?: () => void;
  order?: number;
  source?: Source;
  startPosition?: Position;
  features: Record<string, Feature>;
}

const SelectedFeaturesDialog: React.FC<SelectedFeaturesDialogProps> = ({
  onClose,
  isOpen,
  onMouseDown = (): void => {},
  order = 0,
  source = 'app',
  startPosition = { top: 85, left: 50 },
  features,
}: SelectedFeaturesDialogProps) => {
  const minSize = { width: 400, height: 200 };
  const { t } = useCoreTranslation();

  const stopWheelEventPropagationRef =
    useWheelStopPropagation<HTMLDivElement>();

  return (
    <ToolContainerDraggable
      className="featureInfo"
      data-testid="featureInfoWindow"
      isOpen={isOpen}
      minHeight={minSize.height}
      minWidth={minSize.width}
      onClose={onClose}
      onMouseDown={onMouseDown}
      order={order}
      source={source}
      startPosition={startPosition}
      startSize={{ width: 510, height: 500 }}
      title={t('featureinfo-title')}
    >
      <Box
        data-testid="dataExplorerInfoBox"
        onKeyDown={(event): void => {
          event.stopPropagation();
        }}
        ref={stopWheelEventPropagationRef}
        style={{ paddingTop: '8px', paddingBottom: '8px' }}
      >
        {Object.entries(features).map(([key, value]) => (
          <SelectedFeature feature={value} key={key} layerName={key} />
        ))}
      </Box>
    </ToolContainerDraggable>
  );
};

export default SelectedFeaturesDialog;
